import _ from 'lodash';
import Autosuggest from 'react-autosuggest';
import Highlighter from 'react-highlight-words';
import React, { Component } from 'react';
import toastr from '../../../toastr';

import { Api, IApiResponse } from '../../typings/Api';

export interface ICustomerLookupRecord {
  readonly Address: string;
  readonly City: string;
  readonly Name: string;
  readonly Phone: string;
  readonly State: string;
  readonly Status: string;
  readonly Zip: string;
  readonly itemid: string;
}

interface IProps {
  activeId: string | null;
  disabled: boolean;
  hideInactiveAccounts: boolean;
  hideInvoiceDisabledAccounts?: boolean;
  id: string;
  onCustomerLoad: (customerID: string, reqCashSaleInfoOnCODAccountLoad?: boolean) => void;
  onEnterKeyPressed: Function;
  onPickListLoad: (pickListID: string) => void;
  overflowBreakout?: boolean;
  placeholder?: string;
  showAdditionalNames: boolean;
  skipActiveIDCheck?: boolean;
  style: any;
}

interface IState {
  value: string;
  suggestions: ICustomerLookupRecord[];
}

class CustomerLookup extends Component<IProps, IState> {
  id: string;

  lastLookup: string | null = null;
  overflowBreakout = false;

  state = {
    suggestions: [],
    value: '',
  };

  constructor(props: IProps) {
    super(props);
    this.id = props.id;

    if (props.overflowBreakout) {
      this.overflowBreakout = true;
    }
  }

  set value(value: string) {
    this.setState({
      value,
    });
  }

  clear = () => {
    this.setState({ value: '' });
    this.lastLookup = null;
  };

  // logically only used during a PPC change attempt
  // disable/enableAndFocus are explicit in order to support focus control
  disable = () => {
    const el = document.getElementById(this.id) as HTMLInputElement;
    el.disabled = true;
  };

  enableAndFocus = () => {
    const el = document.getElementById(this.id) as HTMLInputElement;
    el.disabled = false;
    el.focus();
  };

  focus = () => {
    const el = document.getElementById(this.id) as HTMLInputElement;
    el.focus();
  };

  getSuggestions = async (value: string) => {
    // Legacy support for pick lists (leading - or =) for those accustomed to
    // loading via the customer number field.  In this case, don't load suggestions.
    if (value[0] === '-' || value[0] === '=') {
      // remove any existing suggestions
      this.setState({
        suggestions: [],
      });
      return;
    }

    // remove leading ? front counter people are customed to typing
    value = value[0] === '?' ? value.slice(1) : value;

    let res: IApiResponse;

    try {
      res = await Api.get('/list/PCU2', {
        params: {
          limit: 100,
          q: value.trim(),
          skipAttachments: true,
        },
      });

      if (res.status === 200) {
        /* Some customer accounts are flagged to have Invoicing Disabled.  That does not
           mean the account is inactive, it just means the account shouldn't be invoiced
           directly, most likely because it is a ship-to only account.  So we only filter
           Invoice Disabled accounts on the main POS customer lookup control.  For ship to
           lookups throughout PIN1, they are not filtered.
        */
        let suggestions = this.props.hideInvoiceDisabledAccounts
          ? _.get(res, 'data.list', []).filter((o: any) => o.Invoicing !== 'Disabled')
          : res.data.list;

        // Some customers such as 119 have a high volume of inactive accounts and would prefer
        // to not have their customer lookup controls cluttered up.
        if (this.props.hideInactiveAccounts) {
          suggestions = suggestions.filter((o: any) => o.Status !== 'Inactive');
        }

        this.setState({
          suggestions,
        });
      } else {
        throw new Error('200 not received from /list/PCU2');
      }
    } catch (err: any) {
      console.error(err.message);
      toastr.error('Error', 'Customer search temporarily unavailable.');
      this.setState({
        suggestions: [],
      });
    }
  };

  getSuggestionsDebounced = _.debounce(this.getSuggestions, 250); // tslint:disable-line

  getSuggestionValue = (suggestion: ICustomerLookupRecord) => suggestion.itemid;

  // Supplemental Data is used for additional pieces of data in a column such as Name2/Name3
  highlight = (query: string, textToHighlight: string, ...supplementalTextToHighlight: string[]): JSX.Element => {
    let result;
    if (!textToHighlight) {
      result = <div />;
    } else {
      result = (
        <div>
          <Highlighter
            autoEscape={true}
            highlightClassName="hl"
            searchWords={[query]}
            textToHighlight={textToHighlight}
          />
          {supplementalTextToHighlight.map((text, i) => (
            <React.Fragment key={`supTxt-${i}`}>
              <br />
              <Highlighter autoEscape={true} highlightClassName="hl" searchWords={[query]} textToHighlight={text} />
            </React.Fragment>
          ))}
        </div>
      );
    }
    return result;
  };

  // Wraps onCustomerLoad with a duplicative re-fetch check and a field clear check
  loadCustomer = async (customerID: string, reqCashSaleInfoOnCODAccountLoad = true) => {
    // No need to refetch info if suggestion is the same.
    if (customerID !== '' && customerID !== this.props.activeId) {
      const el = document.getElementById(this.id) as HTMLInputElement;
      el.disabled = true;
      await this.props.onCustomerLoad(customerID, reqCashSaleInfoOnCODAccountLoad);
      el.disabled = false;
    }

    if (customerID === '' && this.props.skipActiveIDCheck) {
      await this.props.onCustomerLoad(customerID, reqCashSaleInfoOnCODAccountLoad);
      this.setState({
        value: '',
      });
    }

    if (customerID === '' && this.props.activeId) {
      this.setState({
        value: this.props.activeId,
      });
    }
  };

  /* Exists to support the case of a user directly entering a customer no. and tab-blurring,
     possibly before suggestions even load.

     We utilize a timeout here since React Autosuggest changes the input value on selection,
     but takes a brief instant to do so.

     For example, the user may enter "pepsi" to load suggestions. Upon suggestion selection, the
     input box will change to the customer no, "2000", but only AFTER the blur event is called,
     which in this example would receive "pepsi".  By utilizing a timeout, it will correctly receive
     the resolved suggestion value and detect if onSuggestionSelected already pulled the customer,
     thereby supporting both use cases: utilizing autosuggest as well as rapid tab-blurring.
  */
  onBlur = (syntheticEvent: any) => {
    setTimeout(() => {
      const el = document.getElementById(this.id) as HTMLInputElement;

      // Prevents bomb if dom node has gone away (ex: ship to modal escape by click)
      if (!el) {
        return;
      }

      const customerID = el.value.trim();

      // Prevents duplicative load since onSuggestionSelected creates blur event too
      if (this.lastLookup !== null && customerID === this.lastLookup) {
        return;
      }

      // Legacy pick list support
      if (customerID[0] === '-' || customerID[0] === '=') {
        // Set value back to whatever the currently loaded customer number is (if any)
        this.setState({
          value: this.props.activeId ? this.props.activeId : '',
        });

        // Now pop the pick list modal
        this.props.onPickListLoad(customerID.slice(1));

        return;
      }

      this.loadCustomer(customerID);
    }, 100);
  };

  onChange = (syntheticEvent: any, event: Autosuggest.ChangeEvent) => {
    this.setState({
      value: event.newValue,
    });
  };

  /* Though Autosuggest has its own logic for firing onSuggestionSelected upon the "Enter" key,
   * we're also allowing legacy support for loading pick lists, which essentailly turn off the auto
   * suggest functionality after the leading - or =, so we have to watch for the enter key manually.
   */
  onKeyPress = (syntheticEvent: any) => {
    if (syntheticEvent.key === 'Enter') {
      const el = document.getElementById(this.id) as HTMLInputElement;
      const customerID = el.value.trim();

      if (customerID[0] === '-' || customerID[0] === '=') {
        // Set value back to whatever the currently loaded customer number is (if any)
        this.setState({
          value: this.props.activeId ? this.props.activeId : '',
        });

        this.props.onPickListLoad(customerID.slice(1));
      } else {
        this.props.onEnterKeyPressed();
      }
    }
  };

  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: [],
    });
  };

  onSuggestionsFetchRequested = (req: Autosuggest.SuggestionsFetchRequestedParams) => {
    // req.reason seems to no longer exist in 9.1.x, docs wrong
    // created issue: https://github.com/moroshko/react-autosuggest/issues/386
    this.getSuggestionsDebounced(req.value);
  };

  onSuggestionSelected = (syntheticEvent: any, selection: Autosuggest.SuggestionSelectedEventData<any>) => {
    const customerID = selection.suggestionValue;
    this.lastLookup = customerID;

    // No need to refetch info if suggestion is the same.
    this.loadCustomer(customerID);
  };

  /* This is used to dynamically calculate the height/width of the suggestion container.

     By default, applying a 100% width to the container would constrain it to the size of its
     containing div.  In situations (like shopkeeper) where that div is part of a flex row of
     other divs, the width would be limited significantly.  This logic allows the width to be
     calculated based on the width of the viewport (sans scroll) and "break out" of said
     containing div.

     Specifically for width, it uses all width between the left edge of the container up to 15px
     shy of the right edge of the viewport (sans scroll).

     Specifically for height, the container will always be AT LEAST 150px (minHeight) tall, and
     AT MOST (maxHeight) a height equal to the distance from the top of the container, to the
     bottom of the viewport, minus 75px (roughly size of actionbar footer in shopkeeper).
  */
  renderSuggestionsContainer = ({ containerProps, children }: any) => {
    const style: { maxHeight: string; minHeight: string; position?: string; width: string } = {
      maxHeight: '0px',
      minHeight: '0px',
      width: '0px',
    };

    if (
      containerProps.className ===
      'react-autosuggest__suggestions-container react-autosuggest__suggestions-container--open'
    ) {
      const containerRect = (document.getElementById(containerProps.id) as HTMLElement).getBoundingClientRect();
      style.minHeight = '150px';
      style.maxHeight = `${document.body.clientHeight - containerRect.top - 75}px`;
      style.width = `${document.body.clientWidth - containerRect.left - 15}px`;

      if (this.overflowBreakout) {
        style.position = 'fixed';
      }
    }

    return (
      <div {...containerProps} style={style}>
        {children}
      </div>
    );
  };

  renderSuggestion = (suggestion: ICustomerLookupRecord, { query }: { query: string }) => {
    // Special behavior for inactive customers
    let cityState = '';
    cityState += suggestion.City ? suggestion.City : '';
    cityState += suggestion.State ? (cityState !== '' ? `, ${suggestion.State}` : suggestion.State) : '';

    let className = 'renderSuggestion';
    let onClick;

    if (suggestion.Status === 'Inactive') {
      className += ' suggestion-inactive';
      cityState = '*INACTIVE*';
      onClick = (evt: any) => evt.stopPropagation();
    }

    const additionalNames: string[] = [];
    if (this.props.showAdditionalNames) {
      if (suggestion['Name 2']) {
        additionalNames.push(suggestion['Name 2']);
      }
      if (suggestion['Name 3'] && suggestion['Name 3'] !== suggestion['Name 2']) {
        additionalNames.push(suggestion['Name 3']);
      }
    }

    return (
      <div className={className} onClick={onClick}>
        {this.highlight(query, suggestion.itemid)}
        {this.highlight(query, suggestion.Name, ...additionalNames)}
        {this.highlight(query, suggestion.Address)}
        {this.highlight(query, cityState)}
        {this.highlight(query, suggestion.Phone)}
      </div>
    );
  };

  render() {
    const { value, suggestions } = this.state;

    const inputProps = {
      disabled: this.props.disabled,
      id: this.id,
      onBlur: this.onBlur,
      onChange: this.onChange,
      onKeyPress: this.onKeyPress,
      placeholder: this.props.placeholder,
      style: this.props.style,
      value,
    };

    return (
      <Autosuggest
        getSuggestionValue={this.getSuggestionValue}
        highlightFirstSuggestion={true}
        id={this.id}
        inputProps={inputProps}
        onSuggestionsClearRequested={this.onSuggestionsClearRequested}
        onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
        onSuggestionSelected={this.onSuggestionSelected}
        renderSuggestion={this.renderSuggestion}
        renderSuggestionsContainer={this.renderSuggestionsContainer}
        suggestions={suggestions}
      />
    );
  }
}

export default CustomerLookup;
